import { computed, DestroyRef, inject, Injectable, isDevMode, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { CADS_MAIN_TEMPLATE_ANALYTICS } from '../components/cads-main-template/cads-main-template-analytics';
import type { CadsMainTemplateModalConfig } from '../components/cads-main-template-modal/cads-main-template-modal.config';

import { forceUnlockBodyScroll } from './cads-body-scroll-lock';
import type { CadsGlobalModalGenericErrorOptions, CadsGlobalModalRequest } from './cads-global-modal.config';

const DEFAULT_GENERIC_ERROR_EVENT_NAME = 'network_error';
const DEFAULT_GENERIC_ERROR_TITLE_KEY = 'ERROR_GENERAL_TITLE';
const DEFAULT_GENERIC_ERROR_BODY_KEY = 'ERROR_GENERAL_DESC';
const DEFAULT_GENERIC_ERROR_PRIMARY_CTA_KEY = 'TRY_AGAIN';

/**
 * App-wide CADS modal orchestrator: one active {@link CadsGlobalModalRequest} at a time, rendered by
 * {@link CadsGlobalModalHostComponent}. Independent of legacy `SimpleGlobalModalService`.
 */
@Injectable({ providedIn: 'root' })
export class CadsGlobalModalService {
  private readonly _translate = inject(TranslateService);
  private readonly _analytics = inject(CADS_MAIN_TEMPLATE_ANALYTICS, { optional: true });
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _request = signal<CadsGlobalModalRequest | null>(null);
  private _hostRegistered = false;

  constructor() {
    this._router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this.close();
      });
  }

  /** Active modal request; `null` when closed. Consumed by {@link CadsGlobalModalHostComponent}. */
  readonly request = this._request.asReadonly();

  readonly isOpen = computed(() => this._request() != null);

  /** Called by {@link CadsGlobalModalHostComponent} on init so {@link open} can warn when no host is mounted. */
  registerHost(): void {
    this._hostRegistered = true;
  }

  /**
   * Shows a modal, replacing any current request after a microtask (legacy global-modal parity for teardown).
   * Body content is supplied via {@link CadsGlobalModalRequest.component}.
   */
  open(request: CadsGlobalModalRequest): void {
    if (!this._hostRegistered && isDevMode()) {
      console.warn(
        'CadsGlobalModalService.open() called before a CadsGlobalModalHostComponent was mounted. ' +
          'Add <cads-global-modal-host /> to the app shell.',
      );
    }
    if (this._request() != null && isDevMode()) {
      console.warn('CadsGlobalModalService.open() replacing an already open CADS modal.');
    }
    this.close();
    queueMicrotask(() => {
      this._request.set(request);
    });
  }

  /** Clears the active modal request and restores page scroll. */
  close(): void {
    this._request.set(null);
    forceUnlockBodyScroll();
  }

  /**
   * Replaces modal chrome on the active request; no-op when closed. Use for async CTA flows (submit/retry) where
   * {@link CadsGlobalModalGenericErrorOptions.closeOnPrimary} is **false** and the caller toggles
   * `primaryCta.button.processing`.
   */
  updateModal(modal: CadsMainTemplateModalConfig): void {
    const current = this._request();
    if (!current) {
      return;
    }
    this._request.set({ ...current, modal });
  }

  /** Shorthand: `createGenericErrorModal('transfer_funds_submit')`. */
  createGenericErrorModal(eventName: string): void;
  createGenericErrorModal(options: CadsGlobalModalGenericErrorOptions): void;
  createGenericErrorModal(
    eventNameOrOptions: string | CadsGlobalModalGenericErrorOptions = DEFAULT_GENERIC_ERROR_EVENT_NAME,
  ): void {
    const options = this._normalizeGenericErrorOptions(eventNameOrOptions);
    const eventName = options.eventName ?? DEFAULT_GENERIC_ERROR_EVENT_NAME;
    const title =
      options.title ??
      this._translate.instant(options.titleKey ?? DEFAULT_GENERIC_ERROR_TITLE_KEY);
    const body =
      options.body ??
      this._translate.instant(
        options.bodyKey ?? DEFAULT_GENERIC_ERROR_BODY_KEY,
        options.bodyParams,
      );
    const primaryCtaLabel =
      options.primaryCtaLabel ??
      this._translate.instant(options.primaryCtaKey ?? DEFAULT_GENERIC_ERROR_PRIMARY_CTA_KEY);
    const hasSecondary =
      options.secondaryCtaLabel != null || options.secondaryCtaKey != null;
    const secondaryCtaLabel = hasSecondary
      ? (options.secondaryCtaLabel ??
        this._translate.instant(options.secondaryCtaKey ?? ''))
      : undefined;

    this.open({
      modal: {
        size: options.size ?? 'medium',
        showCloseButton: options.showCloseButton ?? true,
        template: {
          heading: { title },
          primaryCta: {
            skipNavigation: true,
            button: { label: primaryCtaLabel },
          },
          ...(hasSecondary && secondaryCtaLabel
            ? {
                secondaryCta: {
                  skipNavigation: true,
                  button: { label: secondaryCtaLabel, style: 'secondary' as const },
                },
              }
            : {}),
        },
      },
      simpleBody: {
        text: body,
        html: true,
      },
      primaryTapped: () => {
        this._analytics?.logEvent(eventName);
        if (options.closeOnPrimary !== false) {
          this.close();
        }
        options.primaryAction?.();
      },
      ...(hasSecondary
        ? {
            secondaryTapped: () => {
              this._analytics?.logEvent(eventName);
              this.close();
              options.secondaryAction?.();
            },
          }
        : {}),
      dismissed: () => {
        if (options.closeOnDismiss !== false) {
          this.close();
        }
        options.dismissedAction?.();
      },
    });

    this._analytics?.logEvent('error_shown', { 'error id': eventName });
  }

  private _normalizeGenericErrorOptions(
    eventNameOrOptions: string | CadsGlobalModalGenericErrorOptions,
  ): CadsGlobalModalGenericErrorOptions {
    if (typeof eventNameOrOptions === 'string') {
      return { eventName: eventNameOrOptions };
    }
    return { eventName: DEFAULT_GENERIC_ERROR_EVENT_NAME, ...eventNameOrOptions };
  }
}
